/**
 * Safety Equipment Logic Utilities
 * WBC3-verified regulatory logic for Step 5 Safety Equipment
 * Updated to include operations-based equipment triggers per RA verification
 */

import { parseCategory } from '../../../../../../utils/categoryHelpers';

// Get additional equipment requirements based on vessel operations
export const getOperationsBasedEquipment = (draftConfig) => {
  const operationsEquipment = [];
  const operations = draftConfig.operations || [];
  const category = parseCategory(draftConfig.category);

  // Dangerous Goods Operations (WBC3 Section 29)
  if (operations.includes('dangerous_goods')) {
    operationsEquipment.push({
      name: 'Additional Fire Pump (Powered)',
      reason: 'Dangerous goods operations: Additional powered fire pump required (WBC3 29.8.1)',
      reference: 'WBC3 Section 29.8.1',
      category: 'FFE',
      mandatory: true
    });

    operationsEquipment.push({
      name: 'A-60 Fire Insulation',
      reason: 'Dangerous goods operations: Fire-resistant divisions required (WBC3 29.7.3)',
      reference: 'WBC3 Section 29.7.3',
      category: 'FFE',
      mandatory: true
    });

    operationsEquipment.push({
      name: 'DoC DG Certificate',
      reason: 'Dangerous goods operations: Declaration of Compliance certificate required',
      reference: 'IMDG Code + WBC3 Section 29',
      category: 'Documentation',
      mandatory: true
    });
  }

  // Towing Operations (WBC3 Section 26)
  if (operations.includes('towing_operations')) {
    operationsEquipment.push({
      name: 'Towing Winch (WBC3 Spec)',
      reason: 'Towing operations: Winch must comply with WBC3 Table 26.2.1 specifications',
      reference: 'WBC3 Section 26.2.1',
      category: 'Equipment',
      mandatory: true
    });

    operationsEquipment.push({
      name: 'Enhanced Fire Fighting Equipment',
      reason: 'Towing operations: Additional firefighting capacity for towed vessel risks',
      reference: 'WBC3 Section 26 general provisions',
      category: 'FFE',
      mandatory: false
    });
  }

  // Pilot Transfer Operations (WBC3 Section 27)
  if (operations.includes('pilot_transfer')) {
    operationsEquipment.push({
      name: 'Pilot Transfer LSA (Table 27.2.3)',
      reason: 'Pilot transfer operations: Special LSA equipment per Table 27.2.3',
      reference: 'WBC3 Table 27.2.3',
      category: 'LSA',
      mandatory: true
    });

    operationsEquipment.push({
      name: 'Pilot Transfer Ladder',
      reason: 'Pilot transfer operations: Approved pilot transfer arrangements required',
      reference: 'WBC3 Section 27',
      category: 'Equipment',
      mandatory: true
    });
  }

  // Personnel Transfer Operations
  if (operations.includes('personnel_transfer')) {
    operationsEquipment.push({
      name: 'Enhanced Personnel Safety Equipment',
      reason: 'Personnel transfer: Additional safety provisions for transfer operations',
      reference: 'WBC3 general safety provisions',
      category: 'LSA',
      mandatory: false
    });
  }

  // Lifting Operations (LOLER + WBC3)
  if (operations.includes('lifting')) {
    operationsEquipment.push({
      name: 'Lifting Equipment (LOLER Compliant)',
      reason: 'Lifting operations: Equipment must comply with LOLER requirements',
      reference: 'LOLER + WBC3 equipment requirements',
      category: 'Equipment',
      mandatory: true
    });
  }

  // Passenger Operations
  if (operations.includes('passenger_operations')) {
    operationsEquipment.push({
      name: 'Enhanced Passenger LSA',
      reason: 'Passenger operations: Additional lifejackets and safety equipment required',
      reference: 'WBC3 passenger provisions',
      category: 'LSA',
      mandatory: true
    });
  }

  return operationsEquipment;
};

// Calculate mandatory equipment based on WBC3 requirements
export const getMandatoryEquipment = (draftConfig) => {
  const mandatory = [];
  const category = parseCategory(draftConfig.category);
  const vesselType = draftConfig.vesselType || draftConfig.type;
  const baseCertificate = draftConfig.baseCertificate || vesselType;
  const maxPersons = parseInt(draftConfig.maxPersons) || 12;
  
  // Non-Self-Propelled Platforms follow selective assessment (WBC3 26.5.1.1)
  if (baseCertificate === 'non_self_propelled') {
    // Only platform-specific safety equipment per integration guide
    mandatory.push({
      name: 'Emergency Towline (Pre-rigged)',
      reason: 'Platform emergency equipment: Pre-rigged emergency towline suitable for continuing tow (WBC3 26.4.4)',
      reference: 'WBC3 Section 26.4.4',
      category: 'Platform Safety'
    });
    
    mandatory.push({
      name: 'Emergency Anchor and Cable',
      reason: 'Platform emergency equipment: Anchor and cable suitable for holding tow in emergency (WBC3 26.4.4)',
      reference: 'WBC3 Section 26.4.4', 
      category: 'Platform Safety'
    });
    
    mandatory.push({
      name: 'Emergency Lifebuoys with Lines (2)',
      reason: 'Platform emergency equipment: Two lifebuoys with lines for emergency use (WBC3 26.4.4)',
      reference: 'WBC3 Section 26.4.4',
      category: 'Platform Safety'
    });
    
    mandatory.push({
      name: 'Forward End Marking (2000mm x 150mm white bars)',
      reason: 'Platform marking: White bars clearly visible from towing vessel (WBC3 26.5.2.3)',
      reference: 'WBC3 Section 26.5.2.3',
      category: 'Platform Marking'
    });
    
    mandatory.push({
      name: 'Load Line Marking (excluding draught marks)',
      reason: 'Platform marking: Load line marking per Section 13.3 (WBC3 26.5.2.2)',
      reference: 'WBC3 Section 26.5.2.2',
      category: 'Platform Marking'
    });
    
    // Return only platform equipment - no universal WBC3 requirements
    return mandatory;
  }
  
  // Police boats follow different requirements (WBC3 Annex 3)
  if (vesselType === 'police') {
    // Police boat liferafts (special logic for Cat 5-6 dry suit alternative)
    if ((category === 5 || category === 6) && draftConfig.policeDrySuitAlternative) {
      mandatory.push({
        name: 'Dry Suits (instead of liferafts)',
        reason: `Police boats Cat ${category}: Chose dry suit alternative instead of liferafts (Annex 3 11.2.2)`,
        reference: 'WBC3 Annex 3 Section 11.2.2'
      });
    } else {
      mandatory.push({
        name: 'Liferafts (100% POB capacity)',
        reason: `Police boats Cat ${category}: Standard 100% capacity required (Annex 3 Table 11.1)`,
        reference: 'WBC3 Annex 3 Table 11.1'
      });
    }

    // Police boat EPIRB requirements (different from standard WBC3)
    if (category === 2 || category === 3) {
      mandatory.push({
        name: 'EPIRB (406MHz with GPS)',
        reason: `Police boats Cat ${category}: 1 EPIRB required (Annex 3 Table 11.1)`,
        reference: 'WBC3 Annex 3 Table 11.1'
      });
    }

    // Police boat SART requirements (different from standard WBC3)
    if (category === 2 || category === 3) {
      mandatory.push({
        name: 'SART (Search and Rescue Transponder)',
        reason: `Police boats Cat ${category}: 1 SART required (Annex 3 Table 11.1)`,
        reference: 'WBC3 Annex 3 Table 11.1'
      });
    }

    // Police boat pyrotechnics (special quantities)
    mandatory.push({
      name: 'Pyrotechnics (4 parachute + 6 hand + 2 smoke flares)',
      reason: 'Police boats: Special pyrotechnic quantities (Annex 3 Table 11.1)',
      reference: 'WBC3 Annex 3 Table 11.1'
    });

  } else {
    // Standard WBC3 requirements for non-police vessels
    
    // Liferafts - mandatory for ALL categories (0-6) per WBC3 Table 14.1.2
    if (category >= 0 && category <= 6) {
      let capacity = '100%';
      let capacityNote = '';
      
      if (category === 0) {
        capacity = '200%';
        capacityNote = ' (200% POB capacity)';
      } else if (category === 1 && maxPersons >= 16) {
        capacity = '200%';
        capacityNote = ' (200% POB capacity for ≥16 POB)';
      } else {
        capacityNote = ' (100% POB capacity)';
      }
      
      mandatory.push({
        name: `Liferafts${capacityNote}`,
        reason: `Area Category ${category} requirement (WBC3 Table 14.1.2)`,
        reference: 'WBC3 Table 14.1.2'
      });
    }
    
    // EPIRB and SART - mandatory for Area Category 0-2 (WBC3 Table 14.1.2)
    if (category <= 2) {
      mandatory.push({
        name: 'EPIRB (406MHz with GPS)',
        reason: `Area Category ${category} requirement`,
        reference: 'WBC3 Table 14.1.2'
      });
      mandatory.push({
        name: 'SART (Search and Rescue Transponder)',
        reason: `Area Category ${category} requirement`, 
        reference: 'WBC3 Table 14.1.2'
      });
    }
  }
  
  return mandatory;
};

// Determine if immersion suits are mandatory based on WBC3
export const getImmersionSuitRequirement = (draftConfig) => {
  const category = parseCategory(draftConfig.category);
  const vesselType = draftConfig.vesselType || draftConfig.type;
  
  let immersionSuitsMandatory = false;
  let immersionSuitsReason = '';
  
  // WBC3 requirements for immersion suits
  if (draftConfig.operatingArea === 'inland' || draftConfig.category === 'IWW') {
    // Inland waterways - never required
    immersionSuitsMandatory = false;
  } else if (vesselType === 'police') {
    // Police boats - ALL require immersion/dry suits (WBC3 Annex 3 Section 20.3.1)
    immersionSuitsMandatory = true;
    immersionSuitsReason = 'Police boats: Immersion/dry suits required for all persons (Annex 3 20.3.1)';
  } else if (vesselType === 'pilot') {
    // Pilot boats - conditional on category and operations
    if (category <= 5) {
      immersionSuitsMandatory = true;
      immersionSuitsReason = 'Required for Cat 0-5 pilot boats (WBC3 Section 27.2.3)';
    } else if (category === 6 && draftConfig.operatingHours !== 'daylight_only') {
      immersionSuitsMandatory = true;
      immersionSuitsReason = 'Required for Cat 6 pilot boats with night operations';
    }
  } else if (vesselType === 'safety_standby') {
    // Safety/Standby boats - required for rescue operations
    if (category <= 5) {
      immersionSuitsMandatory = true;
      immersionSuitsReason = 'Required for safety/standby boats Cat 0-5 (rescue operations)';
    }
  } else if (category <= 2) {
    // Cold water requirements for Category 0-2 (WBC3 Section 22.2.6.2)
    immersionSuitsMandatory = true; // FIXED: Added missing mandatory flag
    immersionSuitsReason = 'Required for Cat 0-2 vessels in cold waters (WBC3 Section 22.2.6.2)';
  }
  
  return { immersionSuitsMandatory, immersionSuitsReason };
};

// Determine if Thermal Protective Aids (TPAs) are mandatory based on WBC3 14.5.1
export const getThermalProtectiveAidsRequirement = (draftConfig, immersionSuitsMandatory) => {
  const category = parseCategory(draftConfig.category);
  
  let tpaMandatory = false;
  let tpaReason = '';
  let tpaQuantity = '';
  
  // WBC3 14.5.1 & Table 14.1.2 requirements for TPAs
  if (category >= 0 && category <= 5) {
    // Cat 0-5: TPAs mandatory
    if (immersionSuitsMandatory) {
      // Special logic: If immersion suits for all POB → only 2 TPAs needed (WBC3 14.5.6)
      tpaMandatory = true;
      tpaQuantity = '2 for injured persons';
      tpaReason = `Cat ${category}: Reduced requirement - immersion suits provided for all POB (WBC3 14.5.6)`;
    } else {
      tpaMandatory = true;
      tpaQuantity = '100% POB';
      tpaReason = `Cat ${category}: TPAs required for all persons (WBC3 14.5.1)`;
    }
  } else if (category === 6) {
    // Cat 6: Conditional requirements (WBC3 14.5.2)
    // Note: We'll assume cold water operations since we can't determine water temp in wizard
    // Users can deselect if they confirm warm water + canopied liferafts
    tpaMandatory = true;
    tpaQuantity = '100% POB';
    tpaReason = 'Cat 6: Required for cold water or open liferafts (WBC3 14.5.2)';
  }
  
  return { tpaMandatory, tpaReason, tpaQuantity };
};

// Determine if Line Throwing Apparatus is mandatory - ONLY for pilot boats (WBC3 Table 27.2.3)
export const getLineThrowingRequirement = (draftConfig) => {
  const vesselType = draftConfig.vesselType || draftConfig.type;
  
  let lineThrowingMandatory = false;
  let lineThrowingReason = '';
  
  if (vesselType === 'pilot') {
    lineThrowingMandatory = true;
    lineThrowingReason = 'Pilot boats: 2 line throwing appliances required (WBC3 Table 27.2.3 Section 14.1)';
  }
  
  return { lineThrowingMandatory, lineThrowingReason };
};

/**
 * Complete WBC3-Verified MOB Recovery Requirements
 * Implements hierarchical additive requirements based on RA deep dive
 * Universal baseline + vessel-specific additions
 */
export const getMOBRequirements = (vesselConfig) => {
  const mobRequirements = [];
  const vesselType = vesselConfig.vesselType || vesselConfig.type;
  
  // 1. UNIVERSAL BASELINE - ALL SEAGOING VESSELS (WBC3 14.7.1)
  if (vesselConfig.operatingStatus === 'seagoing') {
    mobRequirements.push({
      equipmentId: 'lsa.recovery.equipment',
      name: 'MOB Recovery Equipment',
      description: 'Universal baseline recovery system',
      reason: 'Universal requirement for all seagoing vessels - "An efficient means to enable the recovery of persons (whether conscious or unconscious) from the water"',
      status: 'MANDATORY',
      reference: 'WBC3 14.7.1',
      category: 'universal'
    });
  }
  
  // 2. PILOT BOAT ADDITIONAL REQUIREMENTS (WBC3 Table 27.2.3 & 22.2.3)
  if (vesselType === 'pilot') {
    // Always required for pilot boats
    mobRequirements.push(
      {
        equipmentId: 'lsa.pilot.lifelines',
        name: 'Buoyant Lifelines',
        description: '2 x 18m lines with quoit',
        reason: 'Pilot boat specific requirement for person recovery operations',
        status: 'MANDATORY',
        reference: 'WBC3 Table 27.2.3 14.7',
        category: 'pilot'
      },
      {
        equipmentId: 'lsa.line.throwing.pilot', 
        name: 'Line Throwing Apparatus (Half Set)',
        description: '2 appliances (half set)',
        reason: 'Pilot boat enhanced line throwing capability for rescue operations',
        status: 'MANDATORY',
        reference: 'WBC3 Table 27.2.3',
        category: 'pilot'
      },
      {
        equipmentId: 'lsa.recovery.pilot.transom_steps',
        name: 'Transom Steps + Propeller Guard',
        description: 'Transom-mounted steps with propeller guard for pilot recovery',
        reason: 'WBC3 14.7.1 Requirement: Transom steps with propeller protection for pilot operations',
        status: 'CHOICE_REQUIRED',
        reference: 'WBC3 Table 27.2.3 Section 14.7.1',
        category: 'pilot',
        choiceGroup: 'pilot_recovery_equipment'
      },
      {
        equipmentId: 'lsa.recovery.pilot.transom_ladder',
        name: 'Transom Ladder + Propeller Guard',
        description: 'Transom-mounted ladder with propeller guard for pilot recovery',
        reason: 'WBC3 14.7.1 Requirement: Transom ladder with propeller protection for pilot operations',
        status: 'CHOICE_REQUIRED',
        reference: 'WBC3 Table 27.2.3 Section 14.7.1',
        category: 'pilot',
        choiceGroup: 'pilot_recovery_equipment'
      },
      {
        equipmentId: 'lsa.recovery.pilot.side_ladder',
        name: 'Side Ladder + Propeller Guard',
        description: 'Side-mounted ladder with propeller guard for pilot recovery',
        reason: 'WBC3 14.7.1 Requirement: Side ladder equivalent with propeller protection for pilot operations',
        status: 'CHOICE_REQUIRED',
        reference: 'WBC3 Table 27.2.3 Section 14.7.1',
        category: 'pilot',
        choiceGroup: 'pilot_recovery_equipment'
      },
      {
        equipmentId: 'lsa.recovery.pilot.scrambling_net',
        name: 'Scrambling Net + Propeller Guard',
        description: 'Scrambling net with propeller guard for pilot recovery',
        reason: 'WBC3 14.7.1 Requirement: Scrambling net with propeller protection for pilot operations',
        status: 'CHOICE_REQUIRED',
        reference: 'WBC3 Table 27.2.3 Section 14.7.1',
        category: 'pilot',
        choiceGroup: 'pilot_recovery_equipment'
      }
    );
    
    // Conditional: Fall prevention when no guardrails
    if (!vesselConfig.hasGuardrails) {
      mobRequirements.push({
        equipmentId: 'lsa.mob.prevention.pilot',
        name: 'MOB Fall Prevention Equipment',
        description: 'Fall prevention equipment for vessels without guardrails',
        reason: 'Required when proper guardrails/bulwarks not fitted on pilot boats',
        status: 'MANDATORY',
        reference: 'WBC3 22.2.3',
        category: 'pilot-conditional'
      });
    }
  }
  
  // 3. POLICE BOAT ADDITIONAL REQUIREMENTS (WBC3 Annex 3)
  if (vesselType === 'police') {
    // Always required for police boats
    mobRequirements.push(
      {
        equipmentId: 'lsa.throw.line.police',
        name: 'Additional Throw Line',
        description: '1 additional throw line',
        reason: 'Police boat enhanced throw line capability for enforcement operations',
        status: 'MANDATORY',
        reference: 'WBC3 Annex 3 Table 11.1',
        category: 'police'
      },
      {
        equipmentId: 'lsa.recovery.police.unconscious',
        name: 'Enhanced MOB Recovery (Unconscious)',
        description: 'Enhanced unconscious person recovery capability',
        reason: 'Police boats must be able to recover unconscious persons from enforcement scenarios',
        status: 'MANDATORY',
        reference: 'WBC3 Annex 3 15.3.1',
        category: 'police'
      },
      {
        equipmentId: 'lsa.mob.police.general',
        name: 'General MOB/Rescue Equipment',
        description: 'General MOB and rescue equipment for police operations',
        reason: 'General police MOB equipment for enforcement and rescue operations',
        status: 'MANDATORY',
        reference: 'WBC3 Annex 3 20.4.1',
        category: 'police'
      }
    );
    
    // Conditional: Low freeboard recovery system
    if (vesselConfig.lowFreeboard) {
      mobRequirements.push({
        equipmentId: 'lsa.recovery.police.freeboard',
        name: 'Low Freeboard Recovery System',
        description: 'Practical tested person retrieval system for low freeboard vessels',
        reason: 'Required for police boats with low freeboard that makes standard recovery difficult',
        status: 'MANDATORY',
        reference: 'WBC3 Annex 3 20.4.2',
        category: 'police-conditional'
      });
    }
  }
  
  return mobRequirements;
};

/**
 * Validate MOB equipment compliance
 * Checks that all mandatory MOB equipment is selected, with choice group support
 */
export const validateMOBCompliance = (vesselConfig, selectedEquipment = []) => {
  const requiredMOB = getMOBRequirements(vesselConfig);
  const errors = [];
  
  // Group requirements by choice groups
  const choiceGroups = {};
  const individualRequirements = [];
  
  requiredMOB.forEach(requirement => {
    if (requirement.choiceGroup) {
      if (!choiceGroups[requirement.choiceGroup]) {
        choiceGroups[requirement.choiceGroup] = [];
      }
      choiceGroups[requirement.choiceGroup].push(requirement);
    } else {
      individualRequirements.push(requirement);
    }
  });
  
  // Validate individual requirements
  individualRequirements.forEach(requirement => {
    if (!selectedEquipment.includes(requirement.equipmentId)) {
      errors.push({
        type: 'MOB_COMPLIANCE',
        field: 'mobRecoveryEquipment',
        message: `Missing required MOB equipment: ${requirement.name}`,
        description: requirement.reason,
        reference: requirement.reference,
        equipmentId: requirement.equipmentId,
        category: requirement.category
      });
    }
  });
  
  // Validate choice groups (only one selection required per group)
  Object.entries(choiceGroups).forEach(([groupName, groupRequirements]) => {
    const selectedInGroup = groupRequirements.filter(req => 
      selectedEquipment.includes(req.equipmentId)
    );
    
    if (selectedInGroup.length === 0) {
      errors.push({
        type: 'MOB_COMPLIANCE',
        field: 'mobRecoveryEquipment',
        message: `Missing required choice: ${groupName.replace(/_/g, ' ')}`,
        description: `You must select one option from: ${groupRequirements.map(r => r.name).join(', ')}`,
        reference: groupRequirements[0]?.reference || 'WBC3',
        equipmentId: groupName,
        category: groupRequirements[0]?.category || 'choice'
      });
    } else if (selectedInGroup.length > 1) {
      // This shouldn't happen with proper UI, but just in case
      errors.push({
        type: 'MOB_COMPLIANCE',
        field: 'mobRecoveryEquipment',
        message: `Too many selections in ${groupName.replace(/_/g, ' ')}`,
        description: `Only one option should be selected from: ${groupRequirements.map(r => r.name).join(', ')}`,
        reference: groupRequirements[0]?.reference || 'WBC3',
        equipmentId: groupName,
        category: groupRequirements[0]?.category || 'choice'
      });
    }
  });
  
  // Calculate counts properly considering choice groups
  const individualSelected = individualRequirements.filter(req => selectedEquipment.includes(req.equipmentId)).length;
  const choiceGroupsSelected = Object.values(choiceGroups).filter(group => 
    group.some(req => selectedEquipment.includes(req.equipmentId))
  ).length;
  
  const totalRequiredCount = individualRequirements.length + Object.keys(choiceGroups).length;
  const totalSelectedCount = individualSelected + choiceGroupsSelected;
  
  return {
    isValid: errors.length === 0,
    errors,
    requiredCount: totalRequiredCount,
    selectedCount: totalSelectedCount
  };
};